import { CreditCardOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Divider, Modal, Radio, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from '../config/dayjs';
import axios from '../config/axios';
import { handleAPIError } from '../utils/helpers';

const { Text } = Typography;

interface CreditScoreData {
    network: 'airtel' | 'mtn';
    customer_name: string;
    enquiry_id: string;
    mno_score: number | null;
    mno_band: string;
    mno_rating: string;
    mno_probability_of_default: number | null;
    mno_likelihood_to_default: string;
    mno_months_active: string;
    mno_monthly_turnover: number | null;
    mno_monthly_spend: number | null;
    mno_phone_numbers: { msisdn: string; status: string }[];
    crb_score: number | null;
    crb_band: string;
    crb_rating: string;
    crb_probability_of_default: number | null;
    crb_likelihood_to_default: string;
    crb_open_accounts: number | null;
    crb_adverse_accounts: number | null;
    crb_worst_days_in_arrears: number | null;
    crb_credit_accounts_detail: {
        Total_Value?: { Currency: string; Loan_Amount: string; Loan_Balance: string; Amount_Overdue: string }[];
        Monthly_Installment?: { Currency: string; Amount: string }[];
        Total_Accounts?: number;
        Closed_Accounts?: number;
        Open_Accounts_12Months?: number;
        Worst_Active_Days_in_arrears?: number | null;
    };
    crb_credit_applications_detail: {
        Total_Applications?: { Count: number; Value: number; Currency: string }[];
        Pending_Applications?: { Count: number; Value: number; Currency: string }[];
        Approved_Applications?: { Count: number; Value: number; Currency: string }[];
        Rejected_Applications?: { Count: number; Value: number; Currency: string }[];
    };
    run_by: string | null;
    created_at: string;
    updated_at: string;
}

interface Props {
    userId: number;
    userNin?: string | null;
}

const bandColor = (band: string) =>
    band?.startsWith('A') ? 'green' :
    band?.startsWith('B') ? 'lime' :
    band?.startsWith('C') ? 'gold' :
    band?.startsWith('D') ? 'orange' : 'red';

function formatUGX(v: number | null) {
    if (v == null) return '—';
    return `UGX ${Number(v).toLocaleString()}`;
}

function ScoreCard({
    title,
    score,
    band,
    rating,
    probabilityOfDefault,
    likelihoodToDefault,
    extra,
}: {
    title: string;
    score: number | null;
    band: string;
    rating: string;
    probabilityOfDefault: number | null;
    likelihoodToDefault: string;
    extra?: React.ReactNode;
}) {
    return (
        <Card title={title} size="small" className="flex-1">
            <div className="mb-3 flex items-center gap-3">
                <span className="text-4xl font-bold">{score ?? '—'}</span>
                {band && <Tag color={bandColor(band)} className="text-sm">{band}</Tag>}
            </div>
            {rating && <div className="mb-2 text-sm text-gray-600">{rating}</div>}
            <div className="space-y-1 text-sm">
                {probabilityOfDefault != null && (
                    <div className="flex justify-between">
                        <Text type="secondary">Probability of Default</Text>
                        <span>{probabilityOfDefault}%</span>
                    </div>
                )}
                {likelihoodToDefault && (
                    <div className="flex justify-between">
                        <Text type="secondary">Likelihood to Default</Text>
                        <span>{likelihoodToDefault}</span>
                    </div>
                )}
                {extra}
            </div>
        </Card>
    );
}

export const UserCreditScore = ({ userId, userNin }: Props) => {
    const queryClient = useQueryClient();
    const [network, setNetwork] = useState<'airtel' | 'mtn'>('airtel');

    const { data: scoreData, isLoading } = useQuery<CreditScoreData | null>({
        queryKey: ['credit-score', userId],
        queryFn: async () => {
            const res = await axios.get(`/admin/users/${userId}/credit-score`, { withCredentials: true });
            return res.data.results ?? null;
        },
        enabled: !!userId,
    });

    const runScore = useMutation({
        mutationFn: async () => {
            const res = await axios.post(
                `/admin/users/${userId}/credit-score`,
                { network },
                { withCredentials: true },
            );
            return res.data.results as CreditScoreData;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['credit-score', userId] });
        },
        onError: handleAPIError,
    });

    const noNin = !userNin;

    return (
        <div className="space-y-4">
            {/* Run panel */}
            <Card size="small">
                <div className="flex flex-wrap items-center gap-4">
                    <div>
                        <Text type="secondary" className="mb-1 block text-xs">Customer Network</Text>
                        <Radio.Group
                            value={network}
                            onChange={(e) => setNetwork(e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                            size="small"
                        >
                            <Radio.Button value="airtel">Airtel</Radio.Button>
                            <Radio.Button value="mtn">MTN</Radio.Button>
                        </Radio.Group>
                    </div>
                    <Button
                        type="primary"
                        icon={<CreditCardOutlined />}
                        loading={runScore.isPending}
                        disabled={noNin || isLoading}
                        onClick={() => {
                            if (scoreData) {
                                Modal.confirm({
                                    title: 'Re-run Credit Score?',
                                    content: 'This will overwrite the existing result and incur a new bureau charge. Continue?',
                                    okText: 'Yes, re-run',
                                    okType: 'danger',
                                    cancelText: 'Cancel',
                                    onOk: () => runScore.mutate(),
                                });
                            } else {
                                runScore.mutate();
                            }
                        }}
                    >
                        Run Credit Score
                    </Button>
                    {scoreData && (
                        <Text type="secondary" className="text-xs">
                            Last run by <strong>{scoreData.run_by ?? 'Unknown'}</strong> on{' '}
                            {dayjs(scoreData.updated_at).format('DD MMM YYYY, h:mm A')}
                            {' '}({scoreData.network === 'airtel' ? 'Airtel' : 'MTN'})
                        </Text>
                    )}
                </div>

                {noNin && (
                    <Alert
                        type="warning"
                        showIcon
                        message="This user has no NIN on record — credit score cannot be run"
                        className="mt-3"
                    />
                )}
            </Card>

            {/* Results */}
            {!isLoading && scoreData && (
                <>
                    {scoreData.customer_name && (
                        <Alert
                            type="info"
                            showIcon
                            message={<span>Bureau name: <strong>{scoreData.customer_name}</strong></span>}
                        />
                    )}
                    <div className="flex gap-4">
                        {/* MNO card — Airtel only */}
                        {scoreData.network === 'airtel' ? (
                            <ScoreCard
                                title="MNO Score (Mobile Money)"
                                score={scoreData.mno_score}
                                band={scoreData.mno_band}
                                rating={scoreData.mno_rating}
                                probabilityOfDefault={scoreData.mno_probability_of_default}
                                likelihoodToDefault={scoreData.mno_likelihood_to_default}
                                extra={
                                    <>
                                        <Divider className="my-2" />
                                        <div className="flex justify-between">
                                            <Text type="secondary">Months Active</Text>
                                            <span>{scoreData.mno_months_active || '—'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <Text type="secondary">Monthly Turnover</Text>
                                            <span>{formatUGX(scoreData.mno_monthly_turnover)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <Text type="secondary">Monthly Spend</Text>
                                            <span>{formatUGX(scoreData.mno_monthly_spend)}</span>
                                        </div>
                                        {scoreData.mno_phone_numbers?.length > 0 && (
                                            <>
                                                <Divider className="my-2" />
                                                <Text type="secondary" className="mb-1 block text-xs">Linked Phone Numbers</Text>
                                                <div className="space-y-1">
                                                    {scoreData.mno_phone_numbers.map((p) => (
                                                        <div key={p.msisdn} className="flex items-center justify-between">
                                                            <span className="font-mono text-sm">{p.msisdn}</span>
                                                            <Tag color={p.status === 'Y' ? 'green' : 'default'}>
                                                                {p.status === 'Y' ? 'Active' : 'Inactive'}
                                                            </Tag>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                }
                            />
                        ) : (
                            <Card size="small" className="flex-1">
                                <div className="flex h-full flex-col items-center justify-center gap-2 py-6 text-gray-400">
                                    <InfoCircleOutlined className="text-2xl" />
                                    <span className="text-sm">MNO score not available for MTN</span>
                                </div>
                            </Card>
                        )}

                        {/* CRB card — always */}
                        <ScoreCard
                            title="CRB Score (Credit Bureau)"
                            score={scoreData.crb_score}
                            band={scoreData.crb_band}
                            rating={scoreData.crb_rating}
                            probabilityOfDefault={scoreData.crb_probability_of_default}
                            likelihoodToDefault={scoreData.crb_likelihood_to_default}
                            extra={
                                <>
                                    <Divider className="my-2" />
                                    <div className="flex justify-between">
                                        <Text type="secondary">Open Accounts</Text>
                                        <span>{scoreData.crb_open_accounts ?? '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <Text type="secondary">Adverse Accounts</Text>
                                        <span>{scoreData.crb_adverse_accounts ?? '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <Text type="secondary">Worst Days in Arrears</Text>
                                        <span>{scoreData.crb_worst_days_in_arrears ?? '—'}</span>
                                    </div>
                                </>
                            }
                        />
                    </div>

                    {/* Loan Details */}
                    {(() => {
                        const accts = scoreData.crb_credit_accounts_detail ?? {};
                        const apps = scoreData.crb_credit_applications_detail ?? {};
                        const totalValue = accts.Total_Value?.[0];
                        const monthlyInstallment = accts.Monthly_Installment?.[0];
                        const totalApps = apps.Total_Applications?.[0];
                        const pendingApps = apps.Pending_Applications?.[0];
                        const approvedApps = apps.Approved_Applications?.[0];
                        const rejectedApps = apps.Rejected_Applications?.[0];

                        return (
                            <Card title="Loan Details" size="small">
                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-3">
                                    <div>
                                        <Text type="secondary" className="block text-xs">Total Accounts</Text>
                                        <span className="font-medium">{accts.Total_Accounts ?? '—'}</span>
                                    </div>
                                    <div>
                                        <Text type="secondary" className="block text-xs">Open Accounts</Text>
                                        <span className="font-medium">{scoreData.crb_open_accounts ?? '—'}</span>
                                    </div>
                                    <div>
                                        <Text type="secondary" className="block text-xs">Closed Accounts</Text>
                                        <span className="font-medium">{accts.Closed_Accounts ?? '—'}</span>
                                    </div>
                                    <div>
                                        <Text type="secondary" className="block text-xs">Adverse Accounts</Text>
                                        <span className={`font-medium ${(scoreData.crb_adverse_accounts ?? 0) > 0 ? 'text-red-500' : ''}`}>
                                            {scoreData.crb_adverse_accounts ?? '—'}
                                        </span>
                                    </div>
                                    <div>
                                        <Text type="secondary" className="block text-xs">Worst Days in Arrears</Text>
                                        <span className={`font-medium ${(scoreData.crb_worst_days_in_arrears ?? 0) > 0 ? 'text-orange-500' : ''}`}>
                                            {scoreData.crb_worst_days_in_arrears ?? '—'}
                                        </span>
                                    </div>
                                    <div>
                                        <Text type="secondary" className="block text-xs">Worst Active Days in Arrears</Text>
                                        <span className={`font-medium ${(accts.Worst_Active_Days_in_arrears ?? 0) > 0 ? 'text-orange-500' : ''}`}>
                                            {accts.Worst_Active_Days_in_arrears ?? '—'}
                                        </span>
                                    </div>

                                    {totalValue && (
                                        <>
                                            <div>
                                                <Text type="secondary" className="block text-xs">Total Loan Amount</Text>
                                                <span className="font-medium">{formatUGX(parseFloat(totalValue.Loan_Amount))}</span>
                                            </div>
                                            <div>
                                                <Text type="secondary" className="block text-xs">Outstanding Balance</Text>
                                                <span className={`font-medium ${parseFloat(totalValue.Loan_Balance) > 0 ? 'text-orange-500' : ''}`}>
                                                    {formatUGX(parseFloat(totalValue.Loan_Balance))}
                                                </span>
                                            </div>
                                            <div>
                                                <Text type="secondary" className="block text-xs">Amount Overdue</Text>
                                                <span className={`font-medium ${parseFloat(totalValue.Amount_Overdue) > 0 ? 'text-red-500' : ''}`}>
                                                    {formatUGX(parseFloat(totalValue.Amount_Overdue))}
                                                </span>
                                            </div>
                                        </>
                                    )}

                                    {monthlyInstallment && (
                                        <div>
                                            <Text type="secondary" className="block text-xs">Monthly Installment</Text>
                                            <span className="font-medium">{formatUGX(parseFloat(monthlyInstallment.Amount))}</span>
                                        </div>
                                    )}
                                </div>

                                {(totalApps || pendingApps || approvedApps || rejectedApps) && (
                                    <>
                                        <Divider className="my-3" />
                                        <Text type="secondary" className="mb-2 block text-xs font-medium uppercase tracking-wide">Credit Applications</Text>
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
                                            {totalApps && (
                                                <div>
                                                    <Text type="secondary" className="block text-xs">Total</Text>
                                                    <span className="font-medium">{totalApps.Count} ({formatUGX(totalApps.Value)})</span>
                                                </div>
                                            )}
                                            {approvedApps && (
                                                <div>
                                                    <Text type="secondary" className="block text-xs">Approved</Text>
                                                    <span className="font-medium text-green-600">{approvedApps.Count} ({formatUGX(approvedApps.Value)})</span>
                                                </div>
                                            )}
                                            {pendingApps && (
                                                <div>
                                                    <Text type="secondary" className="block text-xs">Pending</Text>
                                                    <span className={`font-medium ${(pendingApps.Count ?? 0) > 0 ? 'text-orange-500' : ''}`}>
                                                        {pendingApps.Count} ({formatUGX(pendingApps.Value)})
                                                    </span>
                                                </div>
                                            )}
                                            {rejectedApps && (
                                                <div>
                                                    <Text type="secondary" className="block text-xs">Rejected</Text>
                                                    <span className={`font-medium ${(rejectedApps.Count ?? 0) > 0 ? 'text-red-500' : ''}`}>
                                                        {rejectedApps.Count} ({formatUGX(rejectedApps.Value)})
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </Card>
                        );
                    })()}

                    {scoreData.enquiry_id && (
                        <Text type="secondary" className="block text-xs">
                            Enquiry ID: {scoreData.enquiry_id}
                        </Text>
                    )}
                </>
            )}
        </div>
    );
};
